import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Calendar, Mail, MapPin, Facebook, ChevronDown } from "lucide-react";
import { companyInfo } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileSubmenuOpen, setIsMobileSubmenuOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Luk mobilmenuen og dropdowns, når ruten ændres
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    setIsMobileSubmenuOpen(false);
  }, [location]);

  // Luk dropdown ved klik udenfor (desktop)
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.addEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Forhindr scroll, når mobilmenuen er åben
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const workshopItems = [
    { label: "Klargøring til syn", href: "/vaerkstedet/klargoring-til-syn" },
    { label: "Serviceeftersyn", href: "/vaerkstedet/serviceeftersyn" },
    { label: "Fejlsøgning", href: "/vaerkstedet/fejlsogning" },
    { label: "Sommerdæk", href: "/vaerkstedet/sommerdaek" },
    { label: "Vinterdæk", href: "/vaerkstedet/vinterdaek" },
    { label: "Dækopbevaring", href: "/vaerkstedet/daekopbevaring" },
    { label: "Pladeværksted", href: "/vaerkstedet/pladevaerksted" },
    { label: "Olieskift/skylning af automatgearkasse", href: "/vaerkstedet/olieskift-skylning-af-automatgearkasse" },
    { label: "Lånebil", href: "/vaerkstedet/lanebil" }
  ];

  // Tjek om den nuværende sti er en del af værksteds-siderne
  const isWorkshopActive =
    location.pathname === "/vaerkstedet" ||
    location.pathname === "/vaerksted" ||
    workshopItems.some((item) => location.pathname === item.href);

  return (
    <div className="w-full">
      {/* Utility Top Bar (Inspireret af auto-marine.dk) */}
      <div className="bg-neutral-900 text-slate-400 text-xs py-2.5 border-b border-slate-800/50 hidden lg:block">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          {/* Left Side: Contact Info */}
          <div className="flex items-center gap-6">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 hover:text-blue-500 transition-colors"
            >
              <Phone className="h-3.5 w-3.5 text-blue-500" />
              <span>Tlf: <span className="font-mono">{companyInfo.phoneFormatted}</span></span>
            </a>
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-2 hover:text-blue-500 transition-colors"
            >
              <Mail className="h-3.5 w-3.5 text-blue-500" />
              <span>Email: {companyInfo.email}</span>
            </a>
            <span className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-blue-500" />
              <span>Adresse: {companyInfo.address}, {companyInfo.zipCity}</span>
            </span>
          </div>

          {/* Right Side: Social Media */}
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/profile.php?id=100057279183350"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-500 transition-colors"
              title="Følg os på Facebook"
            >
              <Facebook className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-800/80 bg-slate-950/90 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/d6fd363e-e86f-4923-a016-57b110ba59be.svg"
              alt="BRDR. LORENZEN. SKÆRBÆK ApS"
              className="h-7 w-auto object-contain brightness-100 filter"
            />
          </Link>

          {/* Desktop Navigation (Vises kun >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-1">
            {/* Forside */}
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold tracking-wide transition-colors rounded-none uppercase ${
                  isActive
                    ? "text-white border-b-2 border-white"
                    : "text-slate-300 hover:text-white hover:bg-slate-900/30"
                }`
              }
            >
              Forside
            </NavLink>

            {/* Online Booking */}
            <NavLink
              to="/online-booking"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold tracking-wide transition-colors rounded-none uppercase ${
                  isActive
                    ? "text-white border-b-2 border-white"
                    : "text-slate-300 hover:text-white hover:bg-slate-900/30"
                }`
              }
            >
              Online booking
            </NavLink>

            {/* Værkstedet Dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-semibold tracking-wide transition-colors rounded-none uppercase ${
                  isWorkshopActive
                    ? "text-white border-b-2 border-white"
                    : "text-slate-300 hover:text-white hover:bg-slate-900/30"
                }`}
              >
                Værkstedet
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute left-0 mt-0 w-80 rounded-none border border-slate-800 bg-slate-950 py-2 shadow-xl animate-in fade-in slide-in-from-top-2 duration-150">
                  <Link
                    to="/vaerkstedet"
                    className={`block px-5 py-3 text-xs font-bold uppercase tracking-wider border-b border-slate-900/50 hover:bg-slate-900 hover:text-white transition-colors ${
                      location.pathname === "/vaerkstedet" || location.pathname === "/vaerksted"
                        ? "text-blue-500 bg-slate-900/20"
                        : "text-slate-400"
                    }`}
                  >
                    Oversigt: Værkstedet
                  </Link>
                  {workshopItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`block px-5 py-2.5 text-xs font-medium hover:bg-slate-900 hover:text-white transition-colors ${
                        location.pathname === item.href ? "text-blue-500 bg-slate-900/20" : "text-slate-300"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Om os */}
            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold tracking-wide transition-colors rounded-none uppercase ${
                  isActive
                    ? "text-white border-b-2 border-white"
                    : "text-slate-300 hover:text-white hover:bg-slate-900/30"
                }`
              }
            >
              Om os
            </NavLink>

            {/* Kontakt */}
            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold tracking-wide transition-colors rounded-none uppercase ${
                  isActive
                    ? "text-white border-b-2 border-white"
                    : "text-slate-300 hover:text-white hover:bg-slate-900/30"
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Desktop CTAs (Vises kun >= 1200px) */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <Link
              to="/online-booking"
              className="flex items-center gap-2 rounded-none bg-blue-600 px-5 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/10 active:scale-95"
            >
              <Calendar className="h-4 w-4" />
              Online Booking
            </Link>
          </div>

          {/* Mobil/Tablet Menu Knap (Vises kun < 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-4">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex h-10 w-10 items-center justify-center rounded-none border border-slate-800 bg-slate-900/50 text-blue-500 hover:bg-slate-900 transition-colors"
              title="Ring op"
            >
              <Phone className="h-4 w-4" />
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex h-10 w-10 items-center justify-center rounded-none border border-slate-800 bg-slate-900/50 text-slate-300 hover:text-white hover:bg-slate-900 transition-colors"
              aria-label="Åben menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobil/Tablet Navigation Overlay (Vises kun < 1200px) */}
        {isOpen && (
          <div className="fixed inset-x-0 top-20 z-40 min-[1200px]:hidden flex flex-col h-[calc(100vh-5rem)] bg-slate-950/90 backdrop-blur-md animate-in fade-in slide-in-from-top-5 duration-200">
            <div className="flex-1 overflow-y-auto px-6 py-8 space-y-6">
              <div className="flex flex-col gap-1">
                {/* Forside */}
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `block py-3.5 text-base font-semibold tracking-wide border-b border-slate-900 transition-colors uppercase ${
                      isActive ? "text-white" : "text-slate-300"
                    }`
                  }
                >
                  Forside
                </NavLink>

                {/* Online Booking */}
                <NavLink
                  to="/online-booking"
                  className={({ isActive }) =>
                    `block py-3.5 text-base font-semibold tracking-wide border-b border-slate-900 transition-colors uppercase ${
                      isActive ? "text-white" : "text-slate-300"
                    }`
                  }
                >
                  Online booking
                </NavLink>

                {/* Værkstedet Accordion */}
                <div className="border-b border-slate-900 py-3.5">
                  <button
                    onClick={() => setIsMobileSubmenuOpen(!isMobileSubmenuOpen)}
                    className={`flex items-center justify-between w-full text-base font-semibold tracking-wide uppercase transition-colors ${
                      isWorkshopActive ? "text-white" : "text-slate-300"
                    }`}
                  >
                    <span>Værkstedet</span>
                    <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isMobileSubmenuOpen ? "rotate-180" : ""}`} />
                  </button>

                  {/* Accordion Content */}
                  {isMobileSubmenuOpen && (
                    <div className="mt-3 pl-4 space-y-3 border-l border-slate-800 animate-in slide-in-from-top-2 duration-150">
                      <Link
                        to="/vaerkstedet"
                        className={`block py-1 text-sm font-bold uppercase tracking-wider ${
                          location.pathname === "/vaerkstedet" || location.pathname === "/vaerksted"
                            ? "text-blue-500"
                            : "text-slate-400"
                        }`}
                      >
                        Oversigt: Værkstedet
                      </Link>
                      {workshopItems.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={`block py-1 text-sm font-medium ${
                            location.pathname === item.href ? "text-blue-500" : "text-slate-300"
                          }`}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Om os */}
                <NavLink
                  to="/om-os"
                  className={({ isActive }) =>
                    `block py-3.5 text-base font-semibold tracking-wide border-b border-slate-900 transition-colors uppercase ${
                      isActive ? "text-white" : "text-slate-300"
                    }`
                  }
                >
                  Om os
                </NavLink>

                {/* Kontakt */}
                <NavLink
                  to="/kontakt"
                  className={({ isActive }) =>
                    `block py-3.5 text-base font-semibold tracking-wide border-b border-slate-900 transition-colors uppercase ${
                      isActive ? "text-white" : "text-slate-300"
                    }`
                  }
                >
                  Kontakt
                </NavLink>
              </div>

              <div className="pt-6 space-y-4">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center justify-center gap-3 w-full rounded-none border border-slate-800 bg-slate-900/40 py-4 text-base font-bold text-white hover:bg-slate-900 transition-colors"
                >
                  <Phone className="h-5 w-5 text-blue-500" />
                  Ring til os: <span className="font-mono">{companyInfo.phoneFormatted}</span>
                </a>
                <Link
                  to="/online-booking"
                  className="flex items-center justify-center gap-3 w-full rounded-none bg-blue-600 py-4 text-base font-bold text-white hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/10"
                >
                  <Calendar className="h-5 w-5" />
                  Book tid online
                </Link>
              </div>
            </div>

            {/* Info i bunden af mobilmenu */}
            <div className="border-t border-slate-900 bg-slate-950/50 p-6 text-center">
              <p className="text-xs text-slate-500 font-medium">
                {companyInfo.name} · CVR {companyInfo.cvr}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {companyInfo.address}, {companyInfo.zipCity}
              </p>
            </div>
          </div>
        )}
      </header>
    </div>
  );
}
